import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Gavel, Handshake, CheckCircle2, XCircle, Inbox, Clock, ExternalLink, MailCheck,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtEuro(n) {
  const s = (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €`;
}
function daysSince(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}

const KIND_META = {
  formal_notice_proposal:   { icon: Gavel,    ring: "ring-red-500/20",    tint: "bg-red-50 text-red-700 border-red-200",       cta: "bg-red-600 hover:bg-red-700"  },
  partner_handoff_proposal: { icon: Handshake, ring: "ring-orange-500/20", tint: "bg-orange-50 text-orange-700 border-orange-200", cta: "bg-orange-600 hover:bg-orange-700" },
};

const STATUS_STYLE = {
  pending:  "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function PendingActions() {
  const { t } = useTranslation();
  const { refreshPendingCount } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [decision, setDecision] = useState(null); // { action, mode: "approve"|"reject" }
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenant/pending-actions?include_resolved=${showHistory}`);
      setItems(data.actions);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [showHistory]);

  useEffect(() => { load(); }, [load]);

  const pending = useMemo(() => items.filter(x => x.status === "pending"), [items]);
  const resolved = useMemo(() => items.filter(x => x.status !== "pending"), [items]);

  const openDialog = (action, mode) => { setDecision({ action, mode }); setNotes(""); };

  const submit = async () => {
    if (!decision) return;
    setSubmitting(true);
    const { action, mode } = decision;
    try {
      const res = await api.post(
        `/tenant/pending-actions/${action.id}/${mode === "approve" ? "approve" : "reject"}`,
        { notes: notes || null },
      );
      toast.success(mode === "approve"
        ? t("pendingActions.successApproved")
        : t("pendingActions.successRejected"));
      setDecision(null);
      setNotes("");
      await load();
      await refreshPendingCount();
      // Nice: show delivery result on approve
      if (mode === "approve" && res.data?.delivery?.result) {
        const r = res.data.delivery;
        if (r.result === "email_sent") toast.info(`Email envoyé à ${r.to}`);
        else if (r.result === "handoff_prepared") toast.info(`Handoff préparé vers ${r.partner_name}`);
        else if (r.result === "no_email_on_client") toast.warning("Aucun email client — action manuelle requise");
        else if (r.result === "partner_not_configured") toast.warning("Partenaire non configuré — action manuelle requise");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const renderRow = (a) => {
    const meta = KIND_META[a.kind] || KIND_META.formal_notice_proposal;
    const Icon = meta.icon;
    return (
      <TableRow key={a.id} data-testid={`action-row-${a.id}`}>
        <TableCell>
          <div className="flex items-start gap-3">
            <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", meta.tint)}>
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-slate-900">
                {t(`pendingActions.kind.${a.kind}`)}
              </div>
              {a.status !== "pending" && (
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                  {a.status === "approved"
                    ? `${t("pendingActions.approvedBadge")} · ${a.approved_by || "—"} · ${fmtDate(a.approved_at)}`
                    : `${t("pendingActions.rejectedBadge")} · ${a.rejected_by || "—"} · ${fmtDate(a.rejected_at)}`}
                </div>
              )}
              {a.status === "approved" && a.delivery && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <MailCheck className="h-3 w-3" />
                  {a.delivery.result === "email_sent" && `Email → ${a.delivery.to}`}
                  {a.delivery.result === "handoff_prepared" && `Handoff → ${a.delivery.partner_name}`}
                  {a.delivery.result === "no_email_on_client" && "Aucun email client — à traiter manuellement"}
                  {a.delivery.result === "partner_not_configured" && "Partenaire non configuré — à traiter manuellement"}
                </div>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Link to={`/app/invoices/${a.invoice_id}`}
                data-testid={`link-invoice-${a.id}`}
                className="font-mono text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
            {a.invoice_number || "—"} <ExternalLink className="h-3 w-3" />
          </Link>
        </TableCell>
        <TableCell className="text-sm text-slate-800">
          <div>{a.context?.client_name || "—"}</div>
          <div className="text-[11px] text-slate-500">{a.context?.client_email || "—"}</div>
        </TableCell>
        <TableCell className="text-right tabular-nums font-medium text-slate-900">
          {fmtEuro(a.context?.remaining_ttc)}
        </TableCell>
        <TableCell className="text-sm text-slate-500 whitespace-nowrap">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-slate-400" />
            {t("pendingActions.agoDays", { n: daysSince(a.created_at) })}
          </div>
        </TableCell>
        <TableCell className="text-right">
          {a.status === "pending" ? (
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline"
                      data-testid={`reject-${a.id}`}
                      onClick={() => openDialog(a, "reject")}
                      className="border-slate-200 text-slate-700 hover:bg-slate-50">
                <XCircle className="h-3.5 w-3.5 mr-1" /> {t("pendingActions.rejectBtn")}
              </Button>
              <Button size="sm"
                      data-testid={`approve-${a.id}`}
                      onClick={() => openDialog(a, "approve")}
                      className={cn(meta.cta, "text-white")}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("pendingActions.approveBtn")}
              </Button>
            </div>
          ) : (
            <Badge variant="outline" className={cn("text-[10px] uppercase", STATUS_STYLE[a.status])}>
              {a.status === "approved" ? t("pendingActions.approvedBadge") : t("pendingActions.rejectedBadge")}
            </Badge>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const currentMeta = decision ? (KIND_META[decision.action.kind] || KIND_META.formal_notice_proposal) : null;
  const CurrentIcon = currentMeta?.icon;

  return (
    <div className="space-y-6" data-testid="pending-actions-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("pendingActions.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("pendingActions.subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}
                data-testid="toggle-history-btn"
                className="border-slate-200">
          {showHistory ? t("pendingActions.hideHistory") : t("pendingActions.showHistory")}
        </Button>
      </div>

      {pending.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="pending-count-banner">
          <Inbox className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-900 text-xs">
            {pending.length} proposition{pending.length > 1 ? "s" : ""} à valider — aucune n'est envoyée sans votre accord.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-slate-200 bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <Inbox className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">{t("pendingActions.emptyState")}</p>
          </div>
        ) : (
          <Table data-testid="pending-actions-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("pendingActions.col.proposal")}</TableHead>
                <TableHead>{t("pendingActions.col.invoice")}</TableHead>
                <TableHead>{t("pendingActions.col.client")}</TableHead>
                <TableHead className="text-right">{t("pendingActions.col.amount")}</TableHead>
                <TableHead>{t("pendingActions.col.age")}</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map(renderRow)}
              {showHistory && resolved.length > 0 && (
                <>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableCell colSpan={6}
                               className="text-[10px] font-medium uppercase tracking-widest text-slate-500 py-2">
                      Historique
                    </TableCell>
                  </TableRow>
                  {resolved.map(renderRow)}
                </>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent data-testid="decision-dialog" className="max-w-md">
          <DialogHeader>
            {decision && currentMeta && CurrentIcon && (
              <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center mb-2", currentMeta.tint)}>
                <CurrentIcon className="h-5 w-5" />
              </div>
            )}
            <DialogTitle>
              {decision && (decision.mode === "approve"
                ? t("pendingActions.approveTitle", { kind: t(`pendingActions.kind.${decision.action.kind}`).toLowerCase() })
                : t("pendingActions.rejectTitle",  { kind: t(`pendingActions.kind.${decision.action.kind}`).toLowerCase() }))}
            </DialogTitle>
            <DialogDescription>
              {decision?.action && (
                <span>
                  Facture <span className="font-mono">{decision.action.invoice_number}</span> ·
                  {" "}client {decision.action.context?.client_name} ·
                  {" "}reste dû <span className="font-medium">{fmtEuro(decision.action.context?.remaining_ttc)}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("pendingActions.notesLabel")}</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="decision-notes"
                      placeholder={decision?.mode === "approve"
                        ? t("pendingActions.approveNotesPh")
                        : t("pendingActions.rejectNotesPh")} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} data-testid="decision-cancel">
              {t("common.cancel")}
            </Button>
            <Button onClick={submit} disabled={submitting}
                    data-testid="decision-submit"
                    className={cn(decision?.mode === "approve"
                      ? (currentMeta?.cta || "bg-blue-600 hover:bg-blue-700")
                      : "bg-slate-800 hover:bg-slate-900", "text-white")}>
              {submitting ? t("common.loading")
                : (decision?.mode === "approve"
                    ? t("pendingActions.approveBtn")
                    : t("pendingActions.rejectBtn"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
